import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 현장별 조직도 (DOC-50 §6).
 *
 * 배치(`hr_personnel_assignments`)를 기준인원표에 맞춰 세운 값이고, **여기서 사람을 옮길 수는
 * 없다** — 조직도는 배치의 표현이지 입력 화면이 아니다. 옮기는 일은 전술판에서만 한다.
 *
 * `standardFound` 가 false 면 그 현장의 기준인원이 없다는 뜻이고, 그때 공석·초과는 **판정하지
 * 않는다**(`required`·`vacancy` 가 null). "기준이 없으니 인원이 맞다" 고 말하지 않기 위해서다.
 */

export type OrgChartMember = {
  personnelId: string;
  name: string | null;
  rank: string | null;
  grade: string | null;
  fieldName: string | null;
  /** APPOINTEE — 배치 없는 법정 선임자(사용자 지시 2026-10-14). 정원에서는 겸직·파견으로 센다. */
  assignmentRole: 'PRIMARY' | 'CONCURRENT' | 'DISPATCH' | 'APPOINTEE';
  /** 선임만 있는 사람은 배치가 없어 null 이다. */
  employmentType: 'REGULAR' | 'OFFICE_ASSIST' | 'HIGHSCHOOL' | 'PROJECT' | null;
  teamLead: boolean;
};

export type OrgChartGroup = {
  roleTitle: string;
  required: number | null;
  /** 상주 — 주현장. **정원 대비는 이 값**이다. */
  resident: number;
  /** 겸직·파견. 카드에는 그리되 정원에서 세지 않는다. */
  visiting: number;
  /** 공석(양수) · 초과(음수). 기준이 없으면 null. */
  vacancy: number | null;
  members: OrgChartMember[];
};

export type OrgChart = {
  siteId: string;
  siteName: string;
  facilityType: string | null;
  scaleLabel: string | null;
  standardFound: boolean;
  totalRequired: number;
  totalResident: number;
  totalVisiting: number;
  groups: OrgChartGroup[];
};

/** @param former 종결 현장의 떠난 사람까지 — 인력배치표 › 조직도만 켠다(사용자 지시 2026-10-16). */
export function useOrgChart(siteId: string | null, former = false) {
  return useQuery({
    queryKey: ['org-chart', siteId, former],
    queryFn: () => apiFetch<OrgChart>(`/v1/hr/sites/${siteId}/org-chart${former ? '?former=true' : ''}`),
    enabled: !!siteId,
  });
}

export const ASSIGNMENT_ROLE_LABELS: Record<OrgChartMember['assignmentRole'], string> = {
  PRIMARY: '주현장',
  CONCURRENT: '겸직',
  DISPATCH: '파견',
  APPOINTEE: '선임',
};

/** 정규는 적지 않는다 — 대부분이 정규라 글자만 늘어난다(DOC-42 §9와 같은 규칙). */
export const EMPLOYMENT_LABELS: Record<NonNullable<OrgChartMember['employmentType']>, string | null> = {
  REGULAR: null,
  OFFICE_ASSIST: '사무보조',
  HIGHSCHOOL: '고졸',
  PROJECT: 'PJ직',
};
